import { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import { EMPTY_FILTER, filterParamsOf } from '../nucleus/GraphModel';

// THE FILTER'S COUNTS — what each choice in the filter rail would show, so its
// bars are honest histograms. A facet's counts are taken with every OTHER
// filter applied but its own (picking a kind never zeroes the other kinds),
// which is how a faceted search stays explorable.
//
//   personal  GET /nodes/facets — kinds, apps, relationships and mine/shared,
//             under the access, search and date filters (it does not narrow by
//             app or stratum, so those bars are within kind, date and search)
//   a group   GET /groups/<g>/nodes/facets — the same, under the same filters
//             (the route takes every filter the list takes), counted by the
//             server over every node the member may read; never off the first
//             page of rows. Its kinds come as a list; read as the personal map.
//
// The timeline is the dates themselves: one sparse read of the nodes'
// created/updated times (fields=…), bucketed here — the date filter left off,
// so the whole spread stays visible while a range of it is picked. A group's is
// the same read through its own list.

const TIMELINE_LIMIT = 5000;
const TIMELINE_SETTLE = 700;      // ms of quiet before a changed timeline is read again

/** A facets answer in the personal shape: a group's kinds come as a list of
 *  { schema_id, count }; read as a map of id → count. */
export function normalFacets(r) {
    if (!r || typeof r !== 'object') return {};
    const schemas = Array.isArray(r.schemas)
        ? Object.fromEntries(r.schemas.map((x) => [String(x.schema_id ?? '__none__'), x.count || 0]))
        : r.schemas;
    return { ...r, schemas };
}

const without = (filter, keys) => {
    const f = { ...EMPTY_FILTER, ...(filter || {}) };
    keys.forEach((k) => { f[k] = Array.isArray(EMPTY_FILTER[k]) ? [] : EMPTY_FILTER[k]; });
    return f;
};

/** { kinds: [{ id, key, name, count }], apps: [[name, n]], rels: [[type, n]], ownership } — null while read.
 *  `names` is the page's own read of the schemas and their counts
 *  (loadSchemaCounts): the kinds take their names from it rather than reading
 *  the counts a second time, on every filter change. */
export function useFacets({ group, filter, search, tick, names }) {
    const [raw, setRaw] = useState(null);           // { group, k, rest } as read, or { group, failed: true }
    const key = JSON.stringify([group, filter, search, tick]);
    useEffect(() => {
        let live = true;
        const t = setTimeout(async () => {
            try {
                const read = (f) => {
                    const p = new URLSearchParams(group ? filterParamsOf(f, { group }) : { access: f.access || 'owned', ...filterParamsOf(f) });
                    if (search) p.set('search', search);
                    // a read that fails fails the facets: the rail says so, rather
                    // than showing "No links yet." over a server that answered 503
                    return api.request(group ? `/groups/${encodeURIComponent(group)}/nodes/facets?${p}` : `/nodes/facets?${p}`)
                        .then((r) => normalFacets(r));
                };
                // kinds without the kind filter; apps and links within the chosen kinds
                const [k, rest] = await Promise.all([
                    read(without(filter, ['schemas', 'apps', 'rels', 'field', 'value'])),
                    read(without(filter, ['apps', 'rels'])),
                ]);
                if (live) setRaw({ group, k, rest });
            } catch {
                if (live) setRaw({ group, failed: true });
            }
        }, 120);
        return () => { live = false; clearTimeout(t); };
    }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
    return useMemo(() => {
        // another space's read is not this one's: nothing until this space's lands
        if (!raw || !names || raw.group !== group) return null;
        if (raw.failed) return { kinds: [], apps: [], rels: [], ownership: null, failed: true };
        const { k, rest } = raw;
        const nameOf = Object.fromEntries(names.map((s) => [s.key, s.name]));
        // a group whose facets could not be read keeps its kind counts
        const kinds = group && !k?.schemas ? names : Object.entries(k?.schemas || {}).map(([id, count]) => {
            const real = id && id !== '__none__' && id !== '__freeform__' ? id : null;
            return { id: real, key: real || 'none', name: nameOf[real || 'none'] || (real ? 'A schema' : 'Freeform'), count };
        }).sort((a, b) => b.count - a.count);
        const pairs = (m) => (Array.isArray(m) ? m.map((x) => [x.relationship_type || x.type || x.name, x.count || 0]) : Object.entries(m || {}))
            .filter(([n]) => n && n !== 'null').sort((a, b) => b[1] - a[1]);
        // the person's own writes: the route counts them as "User created" and filters them as __none__
        const apps = pairs(rest?.apps).map(([n, c]) => [n === 'User created' ? '__none__' : n, c]);
        return { kinds, apps, rels: pairs(rest?.relationships), ownership: group ? null : k?.ownership || null };
    }, [raw, names, group]);
}

/** The nodes' dates under every filter but the date: { dates: [Date] — null
 *  while read, failed } — `failed` when the read did not come back, so the
 *  timeline says so rather than standing empty. */
export function useTimeline({ group, filter, search, tick }) {
    const [dates, setDates] = useState(null);
    const [failed, setFailed] = useState(false);
    const field = filter?.dateField || 'updated_at';
    // up to 5,000 rows a read: keyed on what the read depends on, so picking a
    // range on the timeline itself never reads it again
    const f = without(filter, ['after', 'before', 'range']);
    const key = JSON.stringify([group, f, search, tick]);
    const read = useRef(false);
    useEffect(() => {
        let live = true;
        // the first read comes quickly; after that it waits for the search to
        // settle, so someone typing with pauses reads it once, not per word
        const wait = read.current ? TIMELINE_SETTLE : 160;
        const t = setTimeout(async () => {
            read.current = true;
            try {
                // one sparse read of every node's dates, through the space's own list
                const p = new URLSearchParams({
                    ...filterParamsOf(f, group ? { group } : undefined), ...(group ? {} : { access: f.access || 'owned' }),
                    fields: 'id,created_at,updated_at', limit: String(TIMELINE_LIMIT), offset: '0', skip_total: 'true', sort: field, order: 'desc',
                });
                if (search) p.set('search', search);
                const r = await api.request(group ? `/groups/${encodeURIComponent(group)}/nodes?${p}` : `/nodes?${p}`);
                const nodes = r?.nodes || [];
                const out = nodes.map((n) => n?.[field] || n?.created_at).filter(Boolean).map((d) => new Date(d)).filter((d) => !Number.isNaN(+d));
                if (live) { setDates(out); setFailed(false); }
            } catch {
                if (live) { setDates([]); setFailed(true); }
            }
        }, wait);
        return () => { live = false; clearTimeout(t); };
    }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
    return useMemo(() => ({ dates, failed }), [dates, failed]);
}

// ── the timeline's buckets ─────────────────────────────────────────────────
const DAY = 86400000;
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const startOfWeek = (d) => { const s = startOfDay(d); const back = (s.getDay() + 6) % 7; return new Date(s.getFullYear(), s.getMonth(), s.getDate() - back); };
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const next = (d, unit) => (unit === 'month' ? new Date(d.getFullYear(), d.getMonth() + 1, 1)
    : new Date(d.getFullYear(), d.getMonth(), d.getDate() + (unit === 'week' ? 7 : 1)));

/** Even buckets across the span of the dates: { unit, buckets: [{ start, end, count }] }. */
export function bucketsOf(dates, now = new Date()) {
    if (!dates?.length) return { unit: 'day', buckets: [] };
    let lo = dates[0];
    dates.forEach((d) => { if (d < lo) lo = d; });
    const span = (now - lo) / DAY;
    const unit = span <= 45 ? 'day' : span <= 420 ? 'week' : 'month';
    const floor = unit === 'day' ? startOfDay : unit === 'week' ? startOfWeek : startOfMonth;
    // at least a fortnight of days, so a young graph still reads as a timeline
    const first = unit === 'day' ? new Date(Math.min(+floor(lo), +startOfDay(now) - 13 * DAY)) : floor(lo);
    const buckets = [];
    for (let s = first; s <= now && buckets.length < 400; s = next(s, unit)) buckets.push({ start: s, end: next(s, unit), count: 0 });
    dates.forEach((d) => {
        const at = floor(d);
        const i = buckets.findIndex((b) => +b.start === +at);
        if (i >= 0) buckets[i].count += 1;
    });
    return { unit, buckets };
}

/** A date as the filter's YYYY-MM-DD, in local time. */
export const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
